// Словарь и мелкие расчёты показа для раздела «Инициативы инженеров».
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО: ни одной денежной формулы. Сумму вознаграждения, долю
// соинициатора, итоги по выборке и разбивку дыр считает ядро (p73i_api.py) — одним
// запросом, согласованными между собой. Вторая реализация той же формулы на фронте
// однажды разойдётся с первой, и разойдётся молча, потому что сверять их некому.
// Особенно это касается ДЕЛЕНИЯ СУММЫ МЕЖДУ СОИНИЦИАТОРАМИ: посчитай его здесь ещё раз —
// и на сделке с тремя людьми экран покажет втрое больше, чем причитается.
//
// Что здесь есть: названия, разряды в числах, склонения и одно правило показа —
// «незнакомого не прячем».

import type {
  ИнициативаСтрока, СвежестьP73I, СправочникиP73I, ЧеловекСтрока,
} from '../api/p73i'
import { plural } from './format'

/** Та же страница, что PAGE в p73i_api.py. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/** Портал 73: там подают заявку. Bitrix: там ставят сумму. Раздел CRM только показывает. */
export const ПОРТАЛ_73_ИНИЦИАТИВЫ = 'https://73.cirspb.com/#/initiatives'
export const BITRIX_СДЕЛКА = 'https://cp.cir.cx/crm/deal/details/'

/** Ссылка на карточку сделки в Bitrix. Пусто — ссылки нет, а не «#0». */
export function ссылкаНаСделку(id: number | null | undefined): string | null {
  const n = Number(id ?? 0)
  return n > 0 ? `${BITRIX_СДЕЛКА}${n}/` : null
}

// ───────────────────────────── числа и слова ─────────────────────────────

/** Число с разрядами: 666737 → «666 737». Пусто — прочерк, а не ноль. */
export function число(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

/** «666 737 ₽». Прочерк, если суммы нет: ноль и «неизвестно» — разные ответы. */
export function рубли(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ₽`
}

/** «13 инициатив», «1 инициатива», «2 инициативы». Разряды и склонение вместе. */
export function инициативы(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'инициатива', 'инициативы', 'инициатив')}`
}

/** «17 заявок», «1 заявка», «2 заявки». */
export function заявки(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'заявка', 'заявки', 'заявок')}`
}

/** «28 сделок», «1 сделка», «2 сделки». */
export function сделки(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'сделка', 'сделки', 'сделок')}`
}

/** «55 человек», «1 человек», «2 человека». */
export function люди(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'человек', 'человека', 'человек')}`
}

/** Месяц «ГГГГ-ММ» словами: «2026-07» → «июль 2026». */
const МЕСЯЦЫ = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

export function месяцСловами(m: string | null | undefined): string {
  const s = String(m ?? '')
  const п = /^(\d{4})-(\d{2})$/.exec(s)
  if (!п) return s || '—'
  const i = Number(п[2]) - 1
  return `${МЕСЯЦЫ[i] ?? п[2]} ${п[1]}`
}

// ───────────────────────────── справочные названия ─────────────────────────────
//
// Расшифровки приезжают из ядра (refs.stage_names и соседи) — чтобы одно и то же
// называлось одинаково в разделе, в выгрузке и в ответе на вопрос «что это было».
// Здесь только правило «незнакомого не прячем»: код, которого нет в справочнике,
// показывается КАК ЕСТЬ. Стадий воронки 4 в зеркале нет вовсе (bx_fields держит только
// основную воронку), и десять из двенадцати попадут на экран кодом — это честнее
// придуманного названия.

export function поСправочнику(
  код: string | number | null | undefined,
  справочник: Record<string, string> | undefined,
  пусто = '—',
): string {
  if (код === null || код === undefined || код === '') return пусто
  return справочник?.[String(код)] ?? String(код)
}

/** Есть ли у стадии человеческое имя. Нет — экран обязан это объяснить, а не молчать. */
export function стадияБезИмени(
  код: string | null | undefined, справочник: Record<string, string> | undefined,
): boolean {
  return !!код && !(справочник && код in справочник)
}

// ───────────────────────────── свежесть зеркала ─────────────────────────────

/**
 * Самая отставшая сущность раздела, а не средняя по двум. Средняя прячет ровно тот
 * случай, ради которого свежесть и показывают: реестр перечитывается каждые пять минут,
 * заявки — раз в четверть часа, и «в среднем свежо» означало бы, что верить можно всему.
 */
export function свежесть(
  список: ВходСвежести[] | undefined,
): { entity: string; age_min: number } | null {
  const с = (список ?? []).filter((x) => x.age_min !== null && x.age_min !== undefined) as {
    entity: string
    age_min: number
  }[]
  if (!с.length) return null
  return с.reduce((a, b) => (b.age_min > a.age_min ? b : a))
}

type ВходСвежести = Pick<СвежестьP73I, 'entity'> & { age_min: number | null }

/** Короткое имя сущности синка для показа человеку. */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'p73i:deals': 'реестр инициатив',
  'p73i:claims': 'заявки инженеров',
}

// ───────────────────────────── инициаторы ─────────────────────────────

/**
 * Имя инициатора для показа. Человека могли уволить, и тогда имени в bx_users нет, а из
 * поля сделки он никуда не делся. Показываем «#id», а не пустоту: пустая ячейка читается
 * как «инициатора не указали», и это была бы неправда ровно про того, кому платить.
 */
export function имяИнициатора(и: { id: number; name: string | null }): string {
  return и.name && и.name.trim() ? и.name : `#${и.id}`
}

/** Все инициаторы строки — инженеры и ремонтники вместе, в порядке источника. */
export function всеИнициаторы(р: ИнициативаСтрока): { id: number; name: string | null }[] {
  return [...(р.eng_people ?? []), ...(р.rem_people ?? [])]
}

// ───────────────────────────── дыры оформления ─────────────────────────────

/**
 * Разбивка дыр в порядке «сколько денег может сгореть», а не по алфавиту. Числа берутся
 * из ответа ядра как есть: складывать их здесь нельзя — одна сделка может попасть сразу
 * в два диагноза, и сумма долей была бы больше числа сделок.
 */
export const ВИДЫ_ДЫР = ['sum_text', 'sum_empty', 'no_who', 'no_date', 'deal_lost'] as const

export type ВидДыры = (typeof ВИДЫ_ДЫР)[number]

/** Цвет плашки диагноза. Отменённая сделка — не ошибка оформления, а факт. */
export function классДиагноза(вид: string): string {
  switch (вид) {
    case 'sum_text':
    case 'sum_empty':
    case 'no_date': return 'badge lose'
    case 'no_who': return 'badge work'
    default: return 'badge'
  }
}

/** Состояние заявки: цвет плашки. Слово берётся из справочника ядра. */
export function классСтатуса(статус: string | null | undefined): string {
  switch (статус) {
    case 'processed': return 'badge won'
    case 'need_estimate': return 'badge lose'
    case 'new': return 'badge work'
    default: return 'badge'
  }
}

/** Зачтена ли инициатива: цвет плашки стадии. */
export function классСтадии(р: { counted: boolean; stage_id: string | null }): string {
  if (р.counted) return 'badge won'
  if (р.stage_id === 'C4:LOSE') return 'badge lose'
  return 'badge work'
}

// ───────────────────── насколько реестр полнее портального ─────────────────────
//
// Портал берёт только сделки с датой инициативы позже 2026-01-01 (initiatives.js:42).
// В ядре лежат все. Разницу считаем ЗДЕСЬ, потому что оба слагаемых приезжают одним
// ответом справочников, а на экране нужна одна фраза, а не два числа.

export function ширеПортала(с: СправочникиP73I | undefined): number | null {
  if (!с?.periods?.length) return null
  const старее = с.periods
    .filter((п) => п.period < '2026-01')
    .reduce((n, п) => n + Number(п.deals ?? 0), 0)
  return старее
}

// ───────────────────────────── свод по людям ─────────────────────────────

/** Кому уже зачли всё, что причиталось. Не «ноль», а «нечего ждать». */
export const всёЗачтено = (ч: ЧеловекСтрока): boolean =>
  Number(ч.pending_rub ?? 0) === 0 && Number(ч.total_rub ?? 0) > 0
